/**
 * Guard faults ride on `routing_leg_faults` and surface on the legs payload
 * as `faults` (#3135 / S4; guard-v2 adds four fault kinds and org scope).
 */
import { GUARD_SIGNALS, guardSignalOfFaultKind, type TargetProbeLegRecord } from '@nadoutong/contracts';

export type SilentFaultMode = 'shadow' | 'act';

/**
 * SKU-global silent fault mode (the shared lamp / dot). An org-scoped silent
 * fault retires the leg for that customer only (R1-A8): it is not this.
 */
export function silentFaultMode(leg: Pick<TargetProbeLegRecord, 'faults'>): SilentFaultMode | null {
  return faultMode(leg, 'silent');
}

/** SKU-global broken fault mode; org-scoped ones are not this (R1-A8). */
export function brokenFaultMode(leg: Pick<TargetProbeLegRecord, 'faults'>): SilentFaultMode | null {
  return faultMode(leg, 'broken');
}

export interface GuardFaultChipSpec {
  readonly faultKind: string;
  readonly mode: SilentFaultMode;
  /** null: SKU-global (every customer); else the one customer it applies to. */
  readonly orgId: string | null;
}

/**
 * R1-A8: one chip per (scope, fault kind) on the leg, act over shadow;
 * SKU-global first, then each customer's, in registry order. Every guard
 * kind (silent and broken included) and every scope is shown, none hidden
 * behind the first.
 */
export function guardFaultChips(leg: Pick<TargetProbeLegRecord, 'faults'>): readonly GuardFaultChipSpec[] {
  const byKey = new Map<string, GuardFaultChipSpec>();
  for (const item of leg.faults ?? []) {
    if (guardSignalOfFaultKind(item.fault_kind) === undefined) continue;
    if (item.mode !== 'act' && item.mode !== 'shadow') continue;
    const orgId = item.scope_kind === 'org' ? (item.scope_id ?? '') : null;
    const key = `${orgId ?? ''}\u0000${item.fault_kind}`;
    const seen = byKey.get(key);
    if (seen === undefined || (seen.mode === 'shadow' && item.mode === 'act')) {
      byKey.set(key, { faultKind: item.fault_kind, mode: item.mode, orgId });
    }
  }
  const order = (faultKind: string): number => GUARD_SIGNALS.findIndex((spec) => spec.faultKind === faultKind);
  return [...byKey.values()].sort((left, right) => {
    if ((left.orgId === null) !== (right.orgId === null)) return left.orgId === null ? -1 : 1;
    const org = (left.orgId ?? '').localeCompare(right.orgId ?? '');
    return org !== 0 ? org : order(left.faultKind) - order(right.faultKind);
  });
}

/** First armed guard fault of a v2-only kind (overload, first content, prelude) on this leg. */
export function guardFault(
  leg: Pick<TargetProbeLegRecord, 'faults'>,
): { readonly faultKind: string; readonly mode: SilentFaultMode; readonly orgId: string | null } | null {
  for (const item of leg.faults ?? []) {
    if (item.fault_kind === 'silent' || item.fault_kind === 'broken') continue;
    if (guardSignalOfFaultKind(item.fault_kind) === undefined) continue;
    if (item.mode !== 'act' && item.mode !== 'shadow') continue;
    return { faultKind: item.fault_kind, mode: item.mode, orgId: item.scope_kind === 'org' ? (item.scope_id ?? null) : null };
  }
  return null;
}

/**
 * Strongest mode over the leg's SKU-global guard faults of every kind. An
 * org-scoped fault retires the leg for that customer only (02-design §12), so
 * it does not turn the shared lamp red.
 */
export function globalGuardMode(leg: Pick<TargetProbeLegRecord, 'faults'>): SilentFaultMode | null {
  let mode: SilentFaultMode | null = null;
  for (const item of leg.faults ?? []) {
    if (item.scope_kind === 'org') continue;
    if (guardSignalOfFaultKind(item.fault_kind) === undefined) continue;
    if (item.mode === 'act') return 'act';
    if (item.mode === 'shadow') mode = 'shadow';
  }
  return mode;
}

function faultMode(
  leg: Pick<TargetProbeLegRecord, 'faults'>,
  kind: 'silent' | 'broken',
): SilentFaultMode | null {
  const faults = leg.faults;
  if (faults === undefined) return null;
  let mode: SilentFaultMode | null = null;
  for (const item of faults) {
    if (item.fault_kind !== kind || item.scope_kind === 'org') continue;
    if (item.mode === 'act') return 'act';
    if (item.mode === 'shadow') mode = 'shadow';
  }
  return mode;
}
